import { useEffect, useState } from 'react';
import { useParams } from 'react-router';

import { Avatar } from '@/components/Avatar';
import { Icon } from '@/components/Icon';
import { Image } from '@/components/Image';
import { Modal } from '@/components/Modal';
import { Spinner } from '@/components/Spinner';
import { useResponsive } from '@/hooks/useResponsive';
import { useTitle } from '@/hooks/useTitle';
import { Flex } from '@/layouts/Flex';
import { Colors } from '@/plugins/data/colors';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { requestPictogram } from '@/store/modules/pictograms';
import styles from './styles.module.scss';

export function PictogramPage() {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const { isDesktop } = useResponsive();
  const { item, loading } = useAppSelector((state) => state.pictograms);

  useTitle(`DienTEA | ${item ? item.title : 'Detalles del pictograma'}`);

  const [modal, setModal] = useState(false);

  useEffect(() => {
    if (id && (!item || item.id !== id)) {
      dispatch(requestPictogram(id));
    }
  }, [item, id, dispatch]);

  if (loading || !item) {
    return <Spinner />;
  }

  return (
    <div className={styles.container}>
      <h1>{item.title}</h1>
      <Flex
        flexDirection={!isDesktop ? 'column-reverse' : 'row'}
        justifyContent='space-evenly'
        gap={32}
        className={styles.pictogramContainer}>
        {!isDesktop && (
          <div style={{ width: '100%' }}>
            {item.tags.length > 0 && (
              <>
                <span className={styles.sectionTitle}>Etiquetas</span>
                <Flex flexWrap='wrap' alignItems='center' gap={8} className={styles.tagsContainer}>
                  {item.tags.map((tag) => (
                    <Flex key={tag.id} className={styles.tag} alignItems='center' gap={6}>
                      <Icon name='tag' size={16} color={Colors.secondaryDark} />
                      <span>{tag.name}</span>
                    </Flex>
                  ))}
                </Flex>
              </>
            )}

            <span className={styles.sectionTitle}>Información adicional</span>
            <Flex alignItems='center' flexWrap='wrap' className={styles.moreInfoContainer}>
              <Flex alignItems='center' gap={12}>
                <Avatar src={item.author?.avatar} size={32} />
                <span>{item.author ? `${item.author.name} ${item.author.surname}` : 'Autor desconocido'}</span>
              </Flex>
              <Flex gap={12} alignItems='center'>
                <div className={styles.iconWrapper}>
                  <Icon name='calendar' size={20} color={Colors.primaryDark} />
                </div>
                <span>{new Date(item.updatedAt).toLocaleDateString('es')}</span>
              </Flex>
            </Flex>
          </div>
        )}

        <Image
          url={item.url}
          alt={item.title}
          serverImage
          className={styles.imageContainer}
          onClick={() => setModal(true)}
        />

        <aside>
          <span className={styles.sectionTitle}>Descripción</span>
          <p style={{ whiteSpace: 'pre-wrap' }}>{item.description}</p>

          {isDesktop && (
            <>
              {item.tags.length > 0 && (
                <>
                  <span className={styles.sectionTitle}>Etiquetas</span>
                  <Flex flexWrap='wrap' alignItems='center' gap={8} className={styles.tagsContainer}>
                    {item.tags.map((tag) => (
                      <Flex key={tag.id} className={styles.tag} alignItems='center' gap={10}>
                        <Icon name='tag' size={16} color={Colors.secondaryDark} />
                        <span>{tag.name}</span>
                      </Flex>
                    ))}
                  </Flex>
                </>
              )}
              <span className={styles.sectionTitle}>Información adicional</span>
              <Flex alignItems='center' flexWrap='wrap' className={styles.moreInfoContainer}>
                <Flex alignItems='center' gap={12}>
                  <Avatar src={item.author?.avatar} size={32} />
                  <span>{item.author ? `${item.author.name} ${item.author.surname}` : 'Autor desconocido'}</span>
                </Flex>
                <Flex gap={12} alignItems='center'>
                  <div className={styles.iconWrapper}>
                    <Icon name='calendar' size={20} color={Colors.primaryDark} />
                  </div>
                  <span>{new Date(item.createdAt).toLocaleDateString('es')}</span>
                </Flex>
              </Flex>
            </>
          )}
        </aside>
      </Flex>

      <Modal isOpen={modal} onClose={() => setModal(false)} maximized>
        <h2 style={{ marginTop: -20, paddingBottom: 12, textAlign: 'center' }}>{item.title}</h2>
        <Image url={item.url} alt={item.title} serverImage lazy />
      </Modal>
    </div>
  );
}
